import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { systemApi } from '@/lib/api'
import { Plus, Edit2, Loader2, Bell, AlertTriangle } from 'lucide-react'

export function WarningSettingsPage() {
  const { t } = useTranslation()
  const [warnings, setWarnings] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [editingWarning, setEditingWarning] = useState<any>(null)
  const [form, setForm] = useState({
    warning_name: '',
    warning_word: '',
    warning_classify: '',
    warning_content: 0,
    warning_match: 0,
    warning_deduplication: 0,
    warning_source: 0,
    receive_time: '',
    weekend_warning: 0,
    warning_interval: 0,
  })

  const loadWarnings = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await systemApi.listWarning(1)
      const list = res?.data || res || []
      setWarnings(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Failed to load warnings:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadWarnings()
  }, [loadWarnings])

  const handleToggleWarning = async (projectId: string, currentStatus: number) => {
    try {
      await systemApi.updateWarningStatus(currentStatus === 1 ? 0 : 1, projectId)
      loadWarnings()
    } catch (err) {
      console.error('Failed to toggle warning:', err)
    }
  }

  const handleSave = async () => {
    if (!form.warning_name.trim()) return
    try {
      await systemApi.updateWarning(form)
      setShowDialog(false)
      loadWarnings()
    } catch (err) {
      console.error('Failed to save warning:', err)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          {t('settings.warning.title')}
        </h2>
        <Button onClick={() => {
          setEditingWarning(null)
          setForm({
            warning_name: '', warning_word: '', warning_classify: '',
            warning_content: 0, warning_match: 0, warning_deduplication: 0,
            warning_source: 0, receive_time: '', weekend_warning: 0, warning_interval: 0,
          })
          setShowDialog(true)
        }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('common.add')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings.warning.warningName')}</TableHead>
                <TableHead>{t('settings.warning.warningWord')}</TableHead>
                <TableHead>{t('settings.warning.warningClassify')}</TableHead>
                <TableHead>{t('settings.warning.warningSwitch')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : warnings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                warnings.map((w) => (
                  <TableRow key={w.project_id || w.id}>
                    <TableCell className="font-medium">{w.warning_name || w.project_name}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{w.warning_word}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{w.warning_classify || t('common.all')}</Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={w.warning_status === 1}
                        onCheckedChange={() => handleToggleWarning(w.project_id, w.warning_status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => {
                        setEditingWarning(w)
                        setForm(w)
                        setShowDialog(true)
                      }}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWarning ? t('settings.warning.edit') : t('common.add')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>{t('settings.warning.warningName')} *</Label>
              <Input
                value={form.warning_name}
                onChange={(e) => setForm({ ...form, warning_name: e.target.value })}
                autoFocus
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>{t('settings.warning.warningWord')}</Label>
              <Input
                value={form.warning_word}
                onChange={(e) => setForm({ ...form, warning_word: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.warning.warningClassify')}</Label>
              <Input
                value={form.warning_classify}
                onChange={(e) => setForm({ ...form, warning_classify: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.warning.warningContent')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.warning_content}
                onChange={(e) => setForm({ ...form, warning_content: Number(e.target.value) })}
              >
                <option value={0}>{t('settings.warning.allContent')}</option>
                <option value={1}>{t('settings.warning.sensitiveOnly')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('settings.warning.warningMatch')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.warning_match}
                onChange={(e) => setForm({ ...form, warning_match: Number(e.target.value) })}
              >
                <option value={0}>{t('monitor.fullText')}</option>
                <option value={1}>{t('monitor.titleOnly')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('settings.warning.warningDeduplication')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.warning_deduplication}
                onChange={(e) => setForm({ ...form, warning_deduplication: Number(e.target.value) })}
              >
                <option value={0}>{t('common.yes')}</option>
                <option value={1}>{t('common.no')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('settings.warning.warningSource')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.warning_source}
                onChange={(e) => setForm({ ...form, warning_source: Number(e.target.value) })}
              >
                <option value={0}>{t('settings.warning.systemPush')}</option>
                <option value={1}>{t('settings.warning.emailPush')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('settings.warning.warningInterval')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.warning_interval}
                onChange={(e) => setForm({ ...form, warning_interval: Number(e.target.value) })}
              >
                <option value={0}>{t('settings.warning.realtime')}</option>
                <option value={1}>{t('settings.warning.scheduled')}</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!form.warning_name.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
